import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useSelector} from '../../../state/hooks';
import {useSyncedPollingSubscriber} from '../../../state/polling/hooks';
import * as RunSelectorRunActions from '../../../state/runSelectorRuns/actions';
import {EMPTY_DATA} from '../../../state/runSelectorRuns/reducer';
import {useCachedDataWithOrigin} from '../../../util/cache';
import {useFirstTimingEventWhen} from '../../../util/profiler/react';
import * as RunTypes from '../../../util/runTypes';
import {RunSelectorRunsPartialVars} from '../types';
import {
  useRunSelectorRunsDataQuery,
  useVariables,
} from './useRunSelectorRunsDataQuery';
import {useRunsPaging} from './useRunsPaging';

export type UseRunSelectorRunsArgs = {
  /** Indicates whether this is a root query for an entire run set, or a sub-query for an expanded group */
  isRootQuery: boolean;
  /** Unique ID for the runs data managed by this instance of useRunSelectorRuns */
  queryId: string;
  /** Determines how many runs will be returned on initial load and when loading more runs */
  pageSize: number;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
  /** If true, will not query for runs */
  skip?: boolean;
};

type RunSelectorRunsResult = {
  /**
   * Indicates whether we're loading runs. Could be from explicit user
   * interactions (e.g. searching, paging, ...) or automatic polling.
   */
  isLoadingRuns: boolean;
  /**
   * Indicates whether we're loading for a polled request.
   * Allows more granular control for loading UI.
   * @todo update type to be required once off of runs-low
   */
  isLoadingRunsPolled?: boolean;
  /**
   * Function for paging. When navigating between pages,
   * call this with the new page number to ensure runs for
   * that page will be loaded (as necessary; may be cached)
   * @todo update type to be required once off of runs-low
   */
  loadMoreRuns?: (page: number) => void;
  /**
   * Detailed runs data. Only contains enough data to
   * satisfy the current paging criteria
   */
  runs: RunTypes.Run[];
  /**
   * Total number of runs that would match the query,
   * regardless of the paging limit
   */
  totalRuns: number;
};

export const useRunSelectorRuns = ({
  isRootQuery,
  queryId,
  pageSize,
  partialVariables,
  shouldOnlyShowSelected,
  skip = false,
}: UseRunSelectorRunsArgs): RunSelectorRunsResult => {
  const {loadMoreRuns, limit} = useRunsPaging(pageSize);

  const {variables, deltaVariablesRef} = useVariables({
    limit,
    partialVariables,
    shouldOnlyShowSelected,
  });
  const {
    data: dataFromQuery,
    isLoadingRuns,
    refetch,
  } = useRunSelectorRunsDataQuery({
    variables,
    skip,
    deltaVariablesRef,
  });

  // Note: Redux is the source of truth for runs data
  // make sure that only redux data is returned from this hook
  // returning data from the query OR the cache opens up race condition bugs
  const dataFromRedux = useSelector(state => state.runSelectorRuns[queryId]);
  const {runs, totalRuns, lastUpdatedAt} = dataFromRedux ?? EMPTY_DATA;

  const {data: dataFromCache, origin} = useCachedDataWithOrigin<
    typeof dataFromRedux
  >({
    namespace: 'RunsSelectorRunsData',
    key: variables,
    dataFromServer: dataFromRedux,
    waitingForDataFromServer: isLoadingRuns && isRootQuery,
  });

  /**
   * Data flow in this hook:
   * 1. When the hook mounts we want to check the cache for previously fetched data
   * 2. If we don't have query data but we have cache data we can put that into redux
   * 3. When the query returns, write the new results into redux and
   * 4. update the cache any time a new query response is received
   */
  const dispatch = useDispatch();
  const queryResults = dataFromQuery?.project?.runs;
  useEffect(() => {
    if (queryResults) {
      dispatch(
        RunSelectorRunActions.processQueryResults(queryId, queryResults)
      );
    }
  }, [dispatch, queryId, queryResults]);

  useEffect(() => {
    /**
     * Since Redux is the source of truth, we only want to load data into Redux from the cache a single time when:
     * 1. data is coming back from the cache, and
     * 2. we don't already have data loaded into redux from a query
     *
     * If the cache can get looped into overwriting Redux when its data conflicts with redux, then the possibility for stale cache writes to UI state is created. If the cache can ONLY seed empty redux data, then stale cache writes will always be soon overwritten by query results being dispatched to Redux (and ultimately syndicated down into the cache)
     */
    if (dataFromCache != null && dataFromRedux == null && isRootQuery) {
      dispatch(
        RunSelectorRunActions.loadRunSelectorRunsFromCache(
          queryId,
          dataFromCache
        )
      );
    }
  }, [dispatch, queryId, dataFromCache, origin, dataFromRedux, isRootQuery]);

  // Clear redux state on unmount/group change
  useEffect(() => {
    return () => {
      if (!skip) {
        dispatch(RunSelectorRunActions.removeQuery(queryId));
      }
    };
  }, [dispatch, queryId, skip]);

  // Perf logging
  const isInitialLoadedRef = useRef(false);
  useFirstTimingEventWhen('run_selector_initial_loaded', () => {
    if (isRootQuery && dataFromRedux != null && !isInitialLoadedRef.current) {
      isInitialLoadedRef.current = true;
      return true;
    }
    return false;
  });

  // Keep delta vars ref up to date
  useEffect(() => {
    deltaVariablesRef.current = {
      currentRuns: runs.map(run => run.id),
      lastUpdated: new Date(lastUpdatedAt).toISOString(),
    };
  }, [runs, lastUpdatedAt, deltaVariablesRef]);

  // Subscribe to synced polling so RunSelector updates in sync with Charts
  const {lastTickedAt, stopTaskTimer} =
    useSyncedPollingSubscriber('RunSelector');
  const [isPollTaskActive, setIsPollTaskActive] = useState(false);

  // Refetch on poll tick
  useEffect(() => {
    if (!skip && lastTickedAt != null && !isLoadingRuns) {
      setIsPollTaskActive(true);
      refetch();
    }
  }, [lastTickedAt]); // eslint-disable-line react-hooks/exhaustive-deps -- should ONLY trigger on poll tick!

  // Record when polled requests are loaded
  useEffect(() => {
    if (!isLoadingRuns && isPollTaskActive) {
      setIsPollTaskActive(false);
      stopTaskTimer();
    }
  }, [isPollTaskActive, isLoadingRuns, stopTaskTimer]);

  const results = useMemo(
    () => ({
      isLoadingRuns: isLoadingRuns && origin === 'server',
      isLoadingRunsPolled: isPollTaskActive,
      loadMoreRuns,
      runs,
      totalRuns,
    }),
    [isLoadingRuns, isPollTaskActive, loadMoreRuns, runs, totalRuns, origin]
  );

  return results;
};
